import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { catchError, EMPTY, firstValueFrom, of, tap } from 'rxjs';
import { IEmployeeSCDItem } from 'src/app/shared/interfaces/IEmployeeSCDItem';
import { INotification } from 'src/app/shared/interfaces/INotification';
import { IScdItem } from 'src/app/shared/interfaces/IScdItem';
import { IScdPickerDialogData } from 'src/app/shared/interfaces/IScdPickerDialogData';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { ScdService } from 'src/app/shared/services/scd.service';
import { scdPickerDialogTableSettings } from 'src/app/shared/configs/scdPicker.config';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatRowDef,
  MatRow,
  MatHeaderRowDef,
  MatHeaderRow,
} from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'app-scd-picker-dialog',
  templateUrl: './scd-picker-dialog.component.html',
  styleUrls: ['./scd-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatProgressSpinner,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatCheckbox,
    MatRowDef,
    MatRow,
    MatHeaderRowDef,
    MatHeaderRow,
    MatDialogActions,
    TranslatePipe,
  ],
})
export class ScdPickerDialogComponent {
  dialogRef = inject<MatDialogRef<ScdPickerDialogComponent>>(MatDialogRef);
  private scdService = inject(ScdService);
  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);
  scdPickerData = inject<IScdPickerDialogData>(MAT_DIALOG_DATA);

  public scdPickerDialogTableSettings = scdPickerDialogTableSettings;
  public filteredEmployeesToSelect: IScdItem[] = [];
  public newlySelectedEmployees: IScdItem[] = [];
  public searchValueFirstName = '';
  public searchValueSurname = '';
  public searchValueEmail = '';
  public scdPickerIsLoading: boolean;

  get didSelectEmployees(): boolean {
    return !!this.newlySelectedEmployees.length;
  }

  public searchUser(): void {
    this.scdPickerIsLoading = true;
    if (this.searchValueFirstName || this.searchValueSurname || this.searchValueEmail) {
      this.scdService
        .searchUserInSCD(this.searchValueFirstName + '*', this.searchValueSurname + '*', this.searchValueEmail)
        .pipe(
          tap(scdResult => {
            this.filterOutSelectedEmployeesFromScdResult(scdResult.records, this.scdPickerData.selectedEmployees);
          }),
          catchError(error => {
            console.error(error);
            this.scdPickerIsLoading = false;
            this.notificationService.showMessage({
              type: 'Error',
              title: 'default.error',
              desc: 'employee-lifecycle.onboarding-search.scd-error',
            });
            return EMPTY;
          }),
        )
        .subscribe();
    } else {
      this.filterOutSelectedEmployeesFromScdResult([], this.scdPickerData.selectedEmployees);
    }
  }

  public clearSearch(): void {
    this.searchValueFirstName = this.searchValueSurname = this.searchValueEmail = '';
    this.filteredEmployeesToSelect = [];
  }

  public addOrRemoveEmployeeToSelection(event: boolean, selectedEmployee: IScdItem): void {
    if (event) {
      if (!this.newlySelectedEmployees.includes(selectedEmployee)) {
        this.newlySelectedEmployees.push(selectedEmployee);
      }
    } else {
      this.newlySelectedEmployees = this.newlySelectedEmployees.filter(
        newlySelectedEmployee => newlySelectedEmployee.gid !== selectedEmployee.gid,
      );
    }
  }

  public employeeIsSelected(employee: IScdItem): boolean {
    return this.newlySelectedEmployees.some(newlySelectedEmployee => newlySelectedEmployee.gid === employee.gid);
  }

  public apply(): void {
    const selectedItems: IEmployeeSCDItem[] = (this.scdPickerData.selectedEmployees ?? []).concat(
      (this.newlySelectedEmployees ?? []).map(employee => this.mapEmployeeListItemToEmployeeSCDItem(employee)),
    );
    this.dialogRef.close(selectedItems);
  }

  public close(): void {
    this.dialogRef.close();
  }

  protected updateSearchValue(searchValue: string, searchField: string): void {
    switch (searchField) {
      case 'searchValueFirstName':
        this.searchValueFirstName = searchValue;
        break;
      case 'searchValueSurname':
        this.searchValueSurname = searchValue;
        break;
      case 'searchValueEmail':
        this.searchValueEmail = searchValue;
        break;
      default:
        break;
    }
  }

  private async throwNotificationByStatus(status: number): Promise<void> {
    let notification: INotification;
    switch (status) {
      case 204:
        notification = {
          type: 'Information',
          title: await firstValueFrom(this.translate.get('shared.picker.scd.search.noResult.title')),
          desc: await firstValueFrom(this.translate.get('shared.picker.scd.search.noResult.desc')),
        };
        break;
      default:
        break;
    }
    this.notificationService.showMessage(notification);
  }

  private filterOutSelectedEmployeesFromScdResult(employeePool: IScdItem[], selectedEmployees: IEmployeeSCDItem[]): void {
    if (!employeePool.length) {
      void this.throwNotificationByStatus(204);
    }
    this.filteredEmployeesToSelect = employeePool.filter(
      employee => !(selectedEmployees ?? []).some(selectedEmployee => selectedEmployee.scdGid === employee.gid),
    );
    this.scdPickerIsLoading = false;
  }

  private mapEmployeeListItemToEmployeeSCDItem(employeeItem: IScdItem): IEmployeeSCDItem {
    return {
      scdName: employeeItem.surName + ', ' + employeeItem.firstName,
      scdEMail: employeeItem.mail,
      scdGid: employeeItem.gid,
      firstName: employeeItem.firstName,
      lastName: employeeItem.surName,
    };
  }
}
